import React, { useState, useEffect } from 'react';
import { Sparkles, X, Activity, AlertTriangle, Lightbulb } from 'lucide-react';

const AiAnalysisModal = ({ isOpen, onClose, athleteName, aiData, isAnalyzing }) => {
  const [hasAcceptedDisclaimer, setHasAcceptedDisclaimer] = useState(() => {
    return localStorage.getItem('hasAcceptedMedicalDisclaimer') === 'true';
  });

  if (!isOpen) return null;

  const handleAcceptDisclaimer = () => {
    localStorage.setItem('hasAcceptedMedicalDisclaimer', 'true');
    setHasAcceptedDisclaimer(true);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
      animation: 'fadeInTab 0.3s ease-out'
    }}>
      {/* Modal Container */}
      <div className="glass-panel" style={{ 
        width: '95%', maxWidth: '600px', maxHeight: '90vh', padding: '0', 
        position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(16, 185, 129, 0.3)', // Greenish subtle glow
        boxShadow: '0 0 40px rgba(16, 185, 129, 0.15)'
      }}>
        
        <button onClick={onClose} style={{
          position: 'absolute', top: '1.2rem', right: '1.2rem', 
          background: 'transparent', border: 'none', color: 'var(--text-muted)', 
          cursor: 'pointer', fontSize: '1.5rem', zIndex: 10
        }}>×</button>

        <div style={{ 
          padding: '1.5rem', borderBottom: '1px solid var(--glass-border)', 
          background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.1) 0%, rgba(255,255,255,0.02) 100%)',
          display: 'flex', alignItems: 'center', gap: '0.8rem', flexShrink: 0
        }}>
          <div style={{ 
            background: 'var(--health-green)', color: '#000', 
            padding: '0.6rem', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 15px var(--health-green)'
          }}>
            <Sparkles size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.3rem' }}>Análisis Biométrico IA</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Evaluando métricas recientes de {athleteName}
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', minHeight: '300px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {!hasAcceptedDisclaimer ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '1.5rem', padding: '1rem' }}>
              <AlertTriangle size={48} color="#ef4444" />
              <h3 style={{ margin: 0, color: '#ef4444', textAlign: 'center' }}>Aviso Importante sobre Salud</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', textAlign: 'center', lineHeight: 1.5 }}>
                Esta aplicación no es un dispositivo médico. Los consejos del AI Coach son puramente informativos y orientados al rendimiento deportivo. Consulta a un médico antes de tomar decisiones sobre tu salud.
              </p>
              <button onClick={handleAcceptDisclaimer} style={{
                background: '#ef4444', color: 'white', border: 'none', padding: '0.8rem 2rem', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginTop: '1rem', transition: 'filter 0.2s'
              }} onMouseOver={e => e.target.style.filter = 'brightness(1.1)'} onMouseOut={e => e.target.style.filter = 'brightness(1)'}>
                Entendido y acepto
              </button>
            </div>
          ) : isAnalyzing ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '2rem 0' }}>
              <div className="ai-loader" style={{ position: 'relative', width: '80px', height: '80px', marginBottom: '1.5rem' }}>
                 {/* CSS Spinner animation injected via style below */}
                 <div className="spinner"></div>
              </div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--health-cyan)', animation: 'pulse 1.5s infinite' }}>
                Procesando datos del atleta...
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', maxWidth: '80%' }}>
                Conectando con Gemini AI para evaluar correlaciones entre carga de entrenamiento, percepción del esfuerzo y métricas de recuperación.
              </p>
            </div>
          ) : !aiData ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <p style={{ color: 'var(--text-muted)' }}>No se pudo obtener el análisis. Inténtalo de nuevo.</p>
            </div>
          ) : (
            <>
              {/* Progreso */}
              <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '1.2rem' }}>
                <h3 style={{ margin: '0 0 0.8rem 0', color: 'var(--health-green)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                  <Activity size={18} /> Progreso y Adaptación
                </h3>
                <p style={{ margin: 0, color: 'white', fontSize: '0.95rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                  {aiData.progress}
                </p>
              </div>

              {/* Alerta */}
              <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '12px', padding: '1.2rem' }}>
                <h3 style={{ margin: '0 0 0.8rem 0', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                  <AlertTriangle size={18} /> Alertas de Fatiga
                </h3>
                <p style={{ margin: 0, color: 'white', fontSize: '0.95rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                  {aiData.alert}
                </p>
              </div>

              {/* Sugerencia */}
              <div style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '12px', padding: '1.2rem' }}>
                <h3 style={{ margin: '0 0 0.8rem 0', color: 'var(--health-cyan)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
                  <Lightbulb size={18} /> Sugerencia de Planificación
                </h3>
                <p style={{ margin: 0, color: 'white', fontSize: '0.95rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                  {aiData.suggestion}
                </p>
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            Powered by <strong>Google Gemini AI</strong>
          </span>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', 
            padding: '0.5rem 1.5rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s'
          }}>Cerrar</button>
        </div>

      </div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        .ai-loader .spinner {
          width: 100%; height: 100%;
          border: 4px solid rgba(16, 185, 129, 0.1);
          border-left-color: var(--health-green);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AiAnalysisModal;
